import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useStore } from '@/app/StoreContext';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { StepDots } from './StepDots';
import { tutorialSteps } from './steps';
import { runtime } from '@/platform/runtime';
import { pushBackButton, popBackButton } from '@/platform/backButton';
import { fx } from '@/platform/feedback';
import { ru } from '@/i18n/ru';
import styles from './Tutorial.module.css';

export const Tutorial = observer(function Tutorial() {
  const { onboarding } = useStore();
  const [index, setIndex] = useState(0);
  const steps = tutorialSteps;
  const isLast = index === steps.length - 1;
  const isFirst = index === 0;
  const isFirstRef = useRef(isFirst);
  isFirstRef.current = isFirst;

  useEffect(() => {
    if (onboarding.isOpen) setIndex(0);
  }, [onboarding.isOpen]);

  const finish = () => {
    fx.accept();
    onboarding.close();
  };

  const next = () => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  };
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  useEffect(() => {
    if (!onboarding.isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onboarding.isOpen, index, isLast]);

  useEffect(() => {
    if (!runtime.isMiniApp || !onboarding.isOpen) return;
    const onBack = () => {
      if (isFirstRef.current) finish();
      else prev();
    };
    pushBackButton(onBack);
    return () => popBackButton(onBack);
  }, [onboarding.isOpen]);

  useEffect(() => {
    if (!onboarding.isOpen) return;
    fx.tap();
  }, [index, onboarding.isOpen]);

  const step = steps[index];

  return (
    <Dialog
      open={onboarding.isOpen}
      onOpenChange={(v) => { if (!v) onboarding.close(); }}
      title={ru.onboarding.dialogTitle}
    >
      <div className={styles.body}>
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={styles.step}
          >
            {step.render()}
          </motion.div>
        </AnimatePresence>
      </div>

      <StepDots total={steps.length} current={index} onJump={setIndex} />

      <div className={styles.footer}>
        <Button variant="ghost" onClick={() => onboarding.close()}>{ru.onboarding.skip}</Button>
        <div className={styles.navRight}>
          {!isFirst && (
            <Button variant="secondary" onClick={prev}>{ru.onboarding.back}</Button>
          )}
          <Button onClick={next}>{isLast ? ru.onboarding.start : ru.onboarding.next}</Button>
        </div>
      </div>
    </Dialog>
  );
});
